/**
 * Burn rate and runway for materials.
 *
 * Consumption is inferred by comparing consecutive monthly snapshots: what was
 * on hand last month, minus what is on hand now. There are no receipts or
 * issue records to work from — the supplier sends a stock count and nothing
 * else — so everything here is an inference from two photographs.
 *
 * That shapes every decision below. The honest failure mode is saying "not
 * enough history" too often; the dangerous one is producing a confident
 * runway from a number that does not mean what it appears to.
 */

export interface StockPoint {
  /** The date the supplier's report covers, not the upload date. */
  reportDate: string;
  stockQty: number;
}

export type BurnConfidence =
  /** Two or more clean periods: the number means something. */
  | 'measured'
  /** Exactly one clean period: directional, not dependable. */
  | 'provisional'
  /** No usable period, so no rate at all. */
  | 'unknown';

export interface BurnRate {
  confidence: BurnConfidence;
  /** Units consumed per day, or null when unknown. Never zero-as-unknown. */
  perDay: number | null;
  perMonth: number | null;
  /** Whole days of stock left at this rate, or null when it cannot be said. */
  runwayDays: number | null;
  /** Periods where stock ROSE, so consumption could not be separated out. */
  restockPeriods: number;
}

const DAY_MS = 86_400_000;

function daysBetween(earlier: string, later: string): number {
  return Math.round((new Date(later).getTime() - new Date(earlier).getTime()) / DAY_MS);
}

/**
 * Turns a material's snapshot history into a burn rate.
 *
 * Points may arrive in any order and are sorted here; a caller passing them
 * newest-first should not silently get a negative rate.
 *
 * PERIODS WHERE STOCK ROSE ARE EXCLUDED, and this is the important judgement.
 * A rise means a delivery landed, and with only two stock counts there is no
 * way to separate "received 500" from "received 500 and consumed 200". Folding
 * such a period in as zero consumption would drag the average DOWN and inflate
 * the runway — reporting more time than there is, on the one screen someone
 * uses to decide whether to reorder. Excluding it makes the rate reflect only
 * the periods where it can actually be read.
 */
export function burnRateOf(points: StockPoint[]): BurnRate {
  const ordered = [...points].sort((a, b) => a.reportDate.localeCompare(b.reportDate));
  const latest = ordered.at(-1);

  if (latest === undefined || ordered.length < 2) {
    return {
      confidence: 'unknown',
      perDay: null,
      perMonth: null,
      runwayDays: null,
      restockPeriods: 0,
    };
  }

  let consumed = 0;
  let days = 0;
  let restockPeriods = 0;

  for (let i = 1; i < ordered.length; i++) {
    const previous = ordered[i - 1];
    const current = ordered[i];
    if (previous === undefined || current === undefined) {
      continue;
    }
    const span = daysBetween(previous.reportDate, current.reportDate);
    // Two reports on the same date tell us nothing about elapsed consumption
    // and would divide by zero.
    if (span <= 0) {
      continue;
    }
    const drop = previous.stockQty - current.stockQty;
    if (drop < 0) {
      restockPeriods += 1;
      continue;
    }
    consumed += drop;
    days += span;
  }

  if (days === 0 || consumed <= 0) {
    // Stock only ever rose or held steady. That is not a burn rate of zero —
    // it is an absence of evidence, and calling it zero would imply the
    // material never runs out.
    return {
      confidence: 'unknown',
      perDay: null,
      perMonth: null,
      runwayDays: null,
      restockPeriods,
    };
  }

  const perDay = consumed / days;
  const cleanPeriods = ordered.length - 1 - restockPeriods;

  return {
    confidence: cleanPeriods >= 2 ? 'measured' : 'provisional',
    perDay,
    perMonth: perDay * 30,
    runwayDays: Math.floor(latest.stockQty / perDay),
    restockPeriods,
  };
}

export type RunwayLevel = 'out' | 'critical' | 'watch' | 'ok' | 'unknown';

export const RUNWAY_THRESHOLD_DAYS = 30;

/**
 * How urgent a material is, for a given stock level.
 *
 * The runway is recomputed from the stock passed in rather than read off
 * burn.runwayDays. An earlier version took a stock quantity and then ignored
 * it, answering from the snapshot the rate was built from — so asking about
 * 250 units got the answer for 700. A function that accepts an argument and
 * quietly disregards it is worse than one that never took it.
 *
 * Depleted is checked first, because a material at zero is out whether or not
 * anybody knows how fast it went.
 */
export function runwayLevel(stockQty: number, burn: BurnRate): RunwayLevel {
  if (stockQty <= 0) {
    return 'out';
  }
  if (burn.perDay === null || burn.perDay <= 0) {
    return 'unknown';
  }
  const days = Math.floor(stockQty / burn.perDay);
  if (days <= RUNWAY_THRESHOLD_DAYS) {
    return 'critical';
  }
  if (days <= RUNWAY_THRESHOLD_DAYS * 2) {
    return 'watch';
  }
  return 'ok';
}

/** Days of stock left at this rate, for any quantity. */
export function runwayDaysFor(stockQty: number, burn: BurnRate): number | null {
  if (burn.perDay === null || burn.perDay <= 0) {
    return null;
  }
  return Math.floor(stockQty / burn.perDay);
}

export const RUNWAY_LABELS: Record<RunwayLevel, string> = {
  out: 'Out of stock',
  critical: 'Under 30 days',
  watch: 'Under 60 days',
  ok: 'Healthy',
  unknown: 'No burn rate yet',
};

/** Rounded for display: spools run in fractions, beads in thousands. */
export function formatRate(perDay: number | null, category: string): string {
  if (perDay === null) {
    return '—';
  }
  const perMonth = perDay * 30;
  return category === 'spool'
    ? `${perMonth.toFixed(1)}/mo`
    : `${Math.round(perMonth).toLocaleString()}/mo`;
}

/** "24 days" / "about 3 months", because 94 days is harder to act on. */
export function formatRunway(days: number | null): string {
  if (days === null) {
    return '—';
  }
  if (days < 60) {
    return `${days} ${days === 1 ? 'day' : 'days'}`;
  }
  const months = Math.round(days / 30);
  return `about ${months} months`;
}
